import type { ContextFamily } from '@quipex/shared/utils';

interface DocumentFileCategory {
    readonly extensions: readonly string[];
    readonly context: ContextFamily;
}

const DOCUMENT_FILE_CATEGORIES = {
    images: {
        extensions: [
            'ai',
            'apng',
            'avif',
            'bmp',
            'cr2',
            'cr3',
            'dng',
            'eps',
            'gif',
            'heic',
            'heif',
            'ico',
            'jfif',
            'jpeg',
            'jpg',
            'jxl',
            'nef',
            'png',
            'psb',
            'psd',
            'raw',
            'svg',
            'tif',
            'tiff',
            'webp',
        ],
        context: 'success',
    },
    documentsAndDrawings: {
        extensions: [
            'dgn',
            'doc',
            'docm',
            'docx',
            'dot',
            'dotm',
            'dotx',
            'dwf',
            'dwfx',
            'dwg',
            'dwt',
            'dxf',
            'eml',
            'epub',
            'msg',
            'odt',
            'ott',
            'pages',
            'rtf',
        ],
        context: 'urgent',
    },
    spreadsheetsAndSchedules: {
        extensions: [
            'csv',
            'mpp',
            'numbers',
            'ods',
            'ots',
            'tsv',
            'xls',
            'xlsb',
            'xlsm',
            'xlsx',
            'xlt',
            'xltm',
            'xltx',
        ],
        context: 'success',
    },
    pdf: {
        extensions: ['pdf'],
        context: 'destructive',
    },
    diagramsAndModels: {
        extensions: [
            '3dm',
            '3ds',
            'bcf',
            'bcfzip',
            'bimx',
            'fbx',
            'glb',
            'gltf',
            'ifc',
            'ifczip',
            'nwc',
            'nwd',
            'obj',
            'rfa',
            'rft',
            'rte',
            'rvt',
            'skp',
            'step',
            'stl',
            'stp',
            'vsd',
            'vsdx',
        ],
        context: 'info',
    },
    presentations: {
        extensions: [
            'key',
            'odp',
            'otp',
            'pot',
            'potm',
            'potx',
            'pps',
            'ppsm',
            'ppsx',
            'ppt',
            'pptm',
            'pptx',
        ],
        context: 'warning',
    },
    web: {
        extensions: ['htm', 'html'],
        context: 'warning',
    },
    media: {
        extensions: [
            '3gp',
            'aac',
            'aif',
            'aifc',
            'aiff',
            'avi',
            'flac',
            'flv',
            'm4a',
            'm4v',
            'mid',
            'midi',
            'mka',
            'mkv',
            'mov',
            'mp3',
            'mp4',
            'mpeg',
            'mpg',
            'ogg',
            'ogv',
            'opus',
            'wav',
            'weba',
            'webm',
            'wma',
            'wmv',
        ],
        context: 'primary',
    },
    archives: {
        extensions: [
            '7z',
            'bz2',
            'gz',
            'lz',
            'lzma',
            'rar',
            'tar',
            'tgz',
            'xz',
            'zip',
            'zst',
            'zstd',
        ],
        context: 'caution',
    },
    textAndData: {
        extensions: [
            'json',
            'log',
            'markdown',
            'md',
            'text',
            'txt',
            'xml',
            'yaml',
            'yml',
        ],
        context: 'secondary',
    },
} as const satisfies Readonly<Record<string, DocumentFileCategory>>;

const DOCUMENT_EXTENSION_CONTEXTS = new Map<string, ContextFamily>(
    Object.values(DOCUMENT_FILE_CATEGORIES).flatMap(({ extensions, context }) =>
        extensions.map((extension) => [extension, context] as const)
    )
);

export function toDocumentExtensionContext(extension: string): ContextFamily {
    return (
        DOCUMENT_EXTENSION_CONTEXTS.get(extension.toLowerCase()) ??
        DOCUMENT_FILE_CATEGORIES.textAndData.context
    );
}
